<template>
  <Transition name="warehouse-modal-overlay">
    <div
      v-if="visible"
      class="warehouse-modal-overlay warehouse-modal-overlay--glass"
      @click.self="$emit('cancel')"
    >
      <Transition name="warehouse-modal-panel" appear>
        <div
          v-if="visible"
          class="warehouse-modal-card warehouse-modal-card--glass warehouse-modal-card--glass-wide"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-warehouse-modal-title"
          @click.stop
        >
          <div class="warehouse-modal-header warehouse-modal-header--glass">
            <h2 id="edit-warehouse-modal-title" class="warehouse-modal-title">Edit Warehouse</h2>
            <button
              type="button"
              class="warehouse-modal-close-btn"
              aria-label="Close"
              title="Close"
              :disabled="saving"
              @click="$emit('cancel')"
            >
              <i class="bi bi-x-lg" aria-hidden="true"></i>
            </button>
          </div>

          <div class="warehouse-modal-body warehouse-modal-body--glass">
            <LabelInputField
              input-id="edit-warehouse-name"
              label="Name"
              v-model="form.name"
              placeholder="Warehouse Name"
              required
              flex
              :disabled="saving"
            />
            <div class="warehouse-modal-textarea-wrap">
              <label for="edit-warehouse-description" class="warehouse-modal-textarea-label">Description</label>
              <textarea
                id="edit-warehouse-description"
                class="warehouse-modal-textarea"
                rows="3"
                placeholder="Warehouse Description (Optional)"
                v-model="form.description"
                :disabled="saving"
              />
            </div>
            <LabelInputField
              input-id="edit-warehouse-address"
              label="Address"
              v-model="form.address"
              placeholder="Street Address"
              flex
              :disabled="saving"
            />
            <div class="warehouse-modal-field-row warehouse-modal-field-row--glass">
              <LabelInputField
                input-id="edit-warehouse-city"
                label="City"
                v-model="form.city"
                placeholder="City"
                flex
                :disabled="saving"
              />
              <LabelInputField
                input-id="edit-warehouse-state"
                label="State"
                v-model="form.state"
                placeholder="State"
                flex
                :disabled="saving"
              />
            </div>
            <div class="warehouse-modal-field-row warehouse-modal-field-row--glass">
              <LabelInputField
                input-id="edit-warehouse-zip"
                label="Zip Code"
                v-model="form.zipCode"
                placeholder="Zip Code"
                flex
                :disabled="saving"
              />
              <LabelInputField
                input-id="edit-warehouse-country"
                label="Country"
                v-model="form.country"
                placeholder="Country"
                flex
                :disabled="saving"
              />
            </div>
            <div class="warehouse-modal-field-row warehouse-modal-field-row--glass">
              <LabelInputField
                input-id="edit-warehouse-phone"
                label="Phone"
                v-model="form.phone"
                placeholder="Phone Number"
                flex
                :disabled="saving"
              />
              <LabelInputField
                input-id="edit-warehouse-email"
                label="Email"
                type="email"
                v-model="form.email"
                placeholder="Email Address"
                flex
                :disabled="saving"
              />
            </div>
          </div>

          <div class="warehouse-modal-actions warehouse-modal-actions--glass">
            <SecondaryButton label="Cancel" :disabled="saving" @click="$emit('cancel')" />
            <PrimaryButton label="Update" :loading="saving" @click="ok" />
          </div>
        </div>
      </Transition>
    </div>
  </Transition>

  <ErrorModal
    :show="showErrorModal"
    title="Validation Error"
    :message="errorMessage"
    buttonText="OK"
    @close="closeErrorModal"
  />
</template>

<script>
import '../styles/AddNewWarehouse.css';
import ErrorModal from '@/components/ErrorModal.vue';
import logger from '@/utils/logger';
import ApiService from '@/services/api.service';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'EditWarehouse',
  components: {
    ErrorModal,
    LabelInputField,
    PrimaryButton,
    SecondaryButton,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    warehouse: {
      type: Object,
      default: null,
    },
    companyId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      form: {
        name: '',
        description: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
        phone: '',
        email: '',
      },
      saving: false,
      showErrorModal: false,
      errorMessage: '',
    };
  },
  watch: {
    warehouse: {
      immediate: true,
      handler(newWarehouse) {
        if (newWarehouse) {
          this.form = {
            name: newWarehouse.name || '',
            description: newWarehouse.description || '',
            address: newWarehouse.address || '',
            city: newWarehouse.city || '',
            state: newWarehouse.state || '',
            zipCode: newWarehouse.zipCode || '',
            country: newWarehouse.country || '',
            phone: newWarehouse.phone || '',
            email: newWarehouse.email || '',
          };
        }
      },
    },
  },
  methods: {
    async ok() {
      if (!this.form.name.trim()) {
        this.errorMessage = 'Warehouse name is required.';
        this.showErrorModal = true;
        return;
      }

      if (!this.warehouse?.id) {
        this.errorMessage = 'No warehouse selected.';
        this.showErrorModal = true;
        return;
      }

      this.saving = true;
      try {
        const response = await ApiService.put(`/warehouses/${this.warehouse.id}`, this.form);
        const updatedWarehouse = response.data.data;
        this.$emit('ok', updatedWarehouse);
      } catch (err) {
        logger.error('Error updating warehouse:', err);

        if (err.response?.data?.errors) {
          this.errorMessage = err.response.data.errors.map((e) => e.message).join(', ');
        } else {
          this.errorMessage = err.response?.data?.message || err.message || 'Failed to update warehouse.';
        }
        this.showErrorModal = true;
      } finally {
        this.saving = false;
      }
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = '';
    },
  },
};
</script>
